/**
 * True Interleaved RNG Stripe Distribution (Frontend)
 * 
 * This utility creates a truly interleaved probability distribution where each item's
 * stripes are evenly spaced across the entire spectrum, rather than clustered.
 * 
 * Example: If item A has 10% probability (10 stripes out of 100), those 10 stripes
 * will be placed at positions 5, 15, 25, 35, 45, 55, 65, 75, 85, 95
 * (evenly distributed across all 100 positions)
 */

/**
 * Build a simple evenly-spaced stripe map
 * Places each item's stripes at fixed intervals, then fills remaining positions
 * @param {Array} allocations - Array of stripe counts for each item [20, 80]
 * @returns {Array} Array of item indices representing the stripe map
 */
export function buildInterleavedStripeMap(allocations) {
  const NUM_STRIPES = 100;
  const stripeMap = Array(NUM_STRIPES).fill(null);
  
  // Place each item's stripes at regular intervals
  for (let itemIndex = 0; itemIndex < allocations.length; itemIndex++) {
    const count = allocations[itemIndex];
    if (count === 0) continue;
    
    const spacing = NUM_STRIPES / count;
    
    // Place this item's stripes at evenly spaced positions
    for (let i = 0; i < count; i++) {
      const position = Math.round((i + 0.5) * spacing);
      const clampedPosition = Math.min(position, NUM_STRIPES - 1);
      
      // If position is already taken, find the nearest empty spot
      if (stripeMap[clampedPosition] === null) {
        stripeMap[clampedPosition] = itemIndex;
      } else {
        // Search outward from ideal position to find empty spot
        let offset = 1;
        let placed = false;
        while (!placed && offset < NUM_STRIPES) {
          // Try right
          if (clampedPosition + offset < NUM_STRIPES && stripeMap[clampedPosition + offset] === null) {
            stripeMap[clampedPosition + offset] = itemIndex;
            placed = true;
          }
          // Try left
          else if (clampedPosition - offset >= 0 && stripeMap[clampedPosition - offset] === null) {
            stripeMap[clampedPosition - offset] = itemIndex;
            placed = true;
          }
          offset++;
        }
      }
    }
  }
  
  // Fill any remaining null positions (shouldn't happen with correct allocations)
  for (let i = 0; i < NUM_STRIPES; i++) {
    if (stripeMap[i] === null) {
      // Find the nearest non-null item
      let left = i - 1;
      let right = i + 1;
      while (left >= 0 || right < NUM_STRIPES) {
        if (left >= 0 && stripeMap[left] !== null) {
          stripeMap[i] = stripeMap[left];
          break;
        }
        if (right < NUM_STRIPES && stripeMap[right] !== null) {
          stripeMap[i] = stripeMap[right];
          break;
        }
        left--;
        right++;
      }
    }
  }
  
  return stripeMap;
}

/**
 * Calculate stripe allocations for items based on their probabilities
 * Ensures allocations sum to exactly NUM_STRIPES
 * @param {Array} items - Array of items with probability values
 * @param {string} probabilityKey - Key to access probability (e.g., 'chance')
 * @returns {Array} Array of stripe counts [10, 40, 50]
 */
export function calculateStripeAllocations(items, probabilityKey = 'chance') {
  const NUM_STRIPES = 100;
  
  // Calculate total probability
  const totalProbability = items.reduce((sum, item) => sum + (Number(item[probabilityKey]) || 0), 0);
  
  if (totalProbability === 0) {
    // If no probabilities set, distribute evenly
    const evenAllocation = Math.floor(NUM_STRIPES / items.length);
    const allocations = items.map(() => evenAllocation);
    const remaining = NUM_STRIPES - (evenAllocation * items.length);
    for (let i = 0; i < remaining; i++) {
      allocations[i]++;
    }
    return allocations;
  }
  
  // Calculate initial allocations based on probability
  const allocations = items.map(item => {
    const itemProbability = (Number(item[probabilityKey]) || 0) / totalProbability;
    return Math.round(itemProbability * NUM_STRIPES);
  });
  
  // Adjust allocations to ensure they sum to exactly NUM_STRIPES
  let totalAllocated = allocations.reduce((sum, count) => sum + count, 0);
  let adjustmentIndex = 0;
  
  while (totalAllocated !== NUM_STRIPES) {
    if (totalAllocated < NUM_STRIPES) {
      allocations[adjustmentIndex]++;
      totalAllocated++;
    } else {
      if (allocations[adjustmentIndex] > 0) {
        allocations[adjustmentIndex]--;
        totalAllocated--;
      }
    }
    adjustmentIndex = (adjustmentIndex + 1) % allocations.length;
  }
  
  return allocations;
}

/**
 * Select an item using true interleaved stripe distribution
 * @param {Array} items - Array of items with probability values
 * @param {number} randomValue - Random value between 0 and 1
 * @param {string} probabilityKey - Key to access probability (e.g., 'chance')
 * @returns {Object} { selectedItem, selectedIndex, stripeIndex, stripeMap, allocations }
 */
export function selectFromInterleavedStripes(items, randomValue, probabilityKey = 'chance') {
  const NUM_STRIPES = 100;
  
  // Calculate stripe allocations
  const allocations = calculateStripeAllocations(items, probabilityKey);
  
  // Build true interleaved stripe map
  const stripeMap = buildInterleavedStripeMap(allocations);
  
  // Determine which stripe the random value lands in
  const stripeIndex = Math.floor(randomValue * NUM_STRIPES);
  const clampedStripeIndex = Math.min(stripeIndex, NUM_STRIPES - 1);
  const selectedIndex = stripeMap[clampedStripeIndex];
  
  return {
    selectedItem: items[selectedIndex],
    selectedIndex,
    stripeIndex: clampedStripeIndex,
    stripeMap,
    allocations
  };
}

